import type { FunctionComponent } from 'react'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@retailer-platform/shared-components'
import { Button } from '@retailer-platform/shared-components/src/tds'
import { FormattedMessage } from 'react-intl'
import { useDashMessages } from '../../../../../intl/intl.hooks'
import { useAdminImpersonation } from './useAdminImpersonation.hooks'

interface TargetAccount {
  canonicalId: string
  givenName?: string
  surname?: string
  email?: string
  canImpersonate: boolean
  impersonateFailureReason?: string | null
}

interface ImpersonationConfirmationModalProps {
  isOpen: boolean
  targetAccount: TargetAccount | null
  onCancel: () => void
  onSuccess?: () => void
}

export const ImpersonationConfirmationModal: FunctionComponent<
  ImpersonationConfirmationModalProps
> = ({ isOpen, targetAccount, onCancel, onSuccess }) => {
  const { startImpersonation, loading } = useAdminImpersonation()

  const i18n = useDashMessages({
    title: 'app.admin.controlPanel.impersonation.confirm.modal.title',
    message: 'app.admin.controlPanel.impersonation.confirm.modal.message',
    confirmButton: 'app.admin.controlPanel.impersonation.confirm.modal.button.confirm',
    cancelButton: 'app.admin.controlPanel.impersonation.confirm.modal.button.cancel',
    noPermissionError: 'app.admin.controlPanel.impersonation.error.noPermission',
    processingButton: 'app.admin.controlPanel.impersonation.confirm.modal.button.processing',
    accountDetails: 'app.admin.controlPanel.impersonation.confirm.modal.accountDetails',
    nameLabel: 'app.admin.controlPanel.impersonation.confirm.modal.name',
    emailLabel: 'app.admin.controlPanel.impersonation.confirm.modal.email',
    uuidLabel: 'app.admin.controlPanel.impersonation.confirm.modal.uuid',
    reasonLabel: 'app.admin.controlPanel.impersonation.confirm.modal.reason',
  })

  if (!targetAccount) {
    return null
  }

  const handleConfirm = async () => {
    await startImpersonation(targetAccount.canonicalId, targetAccount.email)
    if (onSuccess) {
      onSuccess()
    }
  }

  const fullName = `${targetAccount.givenName} ${targetAccount.surname}`
  const canProceed = targetAccount.canImpersonate

  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel}>
      <ModalHeader>{i18n.title}</ModalHeader>
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Text>
            <FormattedMessage
              id="app.admin.controlPanel.impersonation.confirm.modal.message"
              values={{
                name: fullName,
                email: targetAccount.email,
                b: (...chunks) => <span style={{ fontWeight: 'bold' }}>{chunks}</span>,
              }}
            />
          </Text>

          {!canProceed && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#fef3f2',
                border: '1px solid #fda29b',
                borderRadius: '4px',
              }}
            >
              <div css={{ color: '#b42318' }}>
                <Text>{i18n.noPermissionError}</Text>
                {targetAccount.impersonateFailureReason && (
                  <Text size="small" css={{ marginTop: '4px' }}>
                    {i18n.reasonLabel} {targetAccount.impersonateFailureReason}
                  </Text>
                )}
              </div>
            </div>
          )}

          <div
            style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <div style={{ marginBottom: '8px' }}>
              <strong>{i18n.accountDetails}</strong>
            </div>
            <div>
              {i18n.nameLabel} {fullName}
            </div>
            <div>
              {i18n.emailLabel} {targetAccount.email}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {i18n.uuidLabel} {targetAccount.canonicalId}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button.Group>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {i18n.cancelButton}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!canProceed || loading}>
            {loading ? i18n.processingButton : i18n.confirmButton}
          </Button>
        </Button.Group>
      </ModalFooter>
    </Modal>
  )
}
